import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { type OptionItem, OptionType, useGetOptionsQuery } from "@/api/admin";
import { AdminSections } from "@/api/admin/teamRoles";
import {
  type IUser,
  useGetAllUsersQuery,
  USERS_STATUS_VARIANT_MAP,
  usersStatusMap,
} from "@/api/admin/users";
import {
  Loader,
  TableCellBadge,
  TableCellLink,
  TableCellShowAll,
  TableCellText,
  TableHeadCellCustom,
  TableHeadCellSort,
  type TableHeaderType,
} from "@/components/atoms";
import {
  DropDownViewMenu,
  FilterSearch,
  type SubMenuConfig,
} from "@/components/molecules";
import type { SubMenuItem } from "@/components/molecules/FilterSearch/DropDownFilterMenu";
import { PATHS } from "@/config/path";
import { cn } from "@/lib/utils";
import { usePermission } from "@/services/casl/usePermission";
import { DEFAULT_NAME } from "@/utils/constants/defaultNames";
import { createSortHandler } from "@/utils/createSortHandler";
import { formatEnumValue } from "@/utils/formatEnumValue";
import { formatFullName } from "@/utils/formatFullName";
import { formatIsoDate } from "@/utils/formatIsoToUTC";
import { getIdsForFilter } from "@/utils/getIdsForFilter";
import { useGroupedRoles } from "@/utils/hooks/useGroupedRoles";
import { useParamsFromUrl } from "@/utils/hooks/useParamsFromUrl";
import { useTableParams } from "@/utils/hooks/useTableParams";
import { useVisibleFlags } from "@/utils/hooks/useVisibleFlags";
import { parseDateRangeToParams } from "@/utils/parseDateRangeToParams";

import DynamicTable from "../DynamicTable";
import { type ColumnConfig } from "../type";

enum AccountUsersColumn {
  FULL_NAME = "Full name",
  EMAIL = "Email",
  GROUPS = "Groups",
  BRANDS = "Brands",
  BRANCHES = "Branches",
  ACCOUNT_CREATED = "Account created",
  LAST_ACTIVITY = "Last activity",
  USER_STATUS = "User status",
  ROLE = "Role",
}

const TABLE_HEADERS: TableHeaderType = Object.values(AccountUsersColumn);

const NO_ROLE = { id: null, name: "No role" };

const AccountUsersTable = () => {
  const hasPermission = usePermission(AdminSections.PORTAL_USER);

  const [searchValue, setSearchValue] = useState("");
  const { data: optionsData, isPending: isOptionPending } = useGetOptionsQuery([
    OptionType.Groups,
    OptionType.Brands,
    OptionType.Branches,
  ]);

  const { tableParams, handleNext, handlePrev, setTableParams } =
    useTableParams(
      {
        initialParams: {
          query: "",
        },
      },
      searchValue
    );

  const urlParams = useParamsFromUrl(setTableParams, ["roleIds"]);

  const { data: usersData, isPending: isPending } =
    useGetAllUsersQuery(tableParams);

  const { groupedRoles, usersRoleDatas } = useGroupedRoles();

  const allRoles = useMemo(
    () => [NO_ROLE, ...groupedRoles.flatMap(group => group.roles)],
    [groupedRoles]
  );

  const { visibleColumns, setVisibleColumns, flags } =
    useVisibleFlags(TABLE_HEADERS);

  const handleSetFilter = (
    selectedMap: Record<string, string[] | OptionItem[]>
  ) => {
    const selectedStatuses =
      (selectedMap["Status"] as string[] | undefined) ?? [];

    const rawStatuses = selectedStatuses
      .map(status => usersStatusMap[status])
      .filter(Boolean);

    const { dateFrom: createdAtFrom, dateTo: createdAtTo } =
      parseDateRangeToParams(
        selectedMap[AccountUsersColumn.ACCOUNT_CREATED]?.[0],
        true
      );

    const selectedGroups = getIdsForFilter(
      selectedMap[AccountUsersColumn.GROUPS]
    );
    const selectedBrands = getIdsForFilter(
      selectedMap[AccountUsersColumn.BRANDS]
    );
    const selectedBranches = getIdsForFilter(
      selectedMap[AccountUsersColumn.BRANCHES]
    );

    const selectedRoles = getIdsForFilter(selectedMap[AccountUsersColumn.ROLE]);

    setTableParams(prev => ({
      ...prev,
      createdAtFrom,
      createdAtTo,
      groupIds: selectedGroups.length ? selectedGroups : undefined,
      brandIds: selectedBrands.length ? selectedBrands : undefined,
      branchIds: selectedBranches.length ? selectedBranches : undefined,
      status: rawStatuses.length ? rawStatuses : undefined,
      roleIds: selectedRoles.length ? selectedRoles : undefined,
      offset: 0,
    }));
  };

  const handleSortFullName = createSortHandler(setTableParams, "firstName");
  const handleSortCreatedAt = createSortHandler(setTableParams, "createdAt");
  const handleSortLastActivityAt = createSortHandler(
    setTableParams,
    "lastActivityAt"
  );

  const SUB_MENUS = {
    [AccountUsersColumn.GROUPS]: optionsData?.groups ?? [],
    [AccountUsersColumn.BRANDS]: optionsData?.brands ?? [],
    [AccountUsersColumn.BRANCHES]: optionsData?.branches ?? [],
    [AccountUsersColumn.ACCOUNT_CREATED]: [],
    Status: Object.keys(usersStatusMap),
    [AccountUsersColumn.ROLE]: allRoles,
  };

  const MENU_LIST = Object.keys(SUB_MENUS);

  const SUB_MENU_REGISTRY: Record<string, SubMenuConfig> = {
    [AccountUsersColumn.GROUPS]: {
      type: "search",
      placeholder: "Search groups",
    },
    [AccountUsersColumn.BRANDS]: {
      type: "search",
      placeholder: "Search brands",
    },
    [AccountUsersColumn.BRANCHES]: {
      type: "search",
      placeholder: "Search branches",
    },
    [AccountUsersColumn.ACCOUNT_CREATED]: { type: "date" },
    [AccountUsersColumn.ROLE]: {
      type: "search",
      placeholder: "Search roles",
    },
    Status: { type: "status" },
  };

  const shouldAlignTopFor = (
    row: IUser,
    current: "branches" | "brands" | "groups"
  ) => {
    const map = {
      branches: row.branches.length,
      brands: row.brands.length,
      groups: row.groups.length,
    };

    const currentLen = map[current];
    const otherLens = Object.entries(map)
      .filter(([key]) => key !== current)
      .map(([, len]) => len);

    return currentLen === 1 && otherLens.some(len => len > 1);
  };

  const ENTITY_COLUMN_CONFIG = {
    width: "w-[11%]",
    alignClass: "align-top",
  };

  const columns: ColumnConfig<IUser>[] = [
    {
      key: "firstName",
      label: AccountUsersColumn.FULL_NAME,
      visible: flags[AccountUsersColumn.FULL_NAME],
      headerComponent: ({ label }) => (
        <TableHeadCellSort
          sortOrder={tableParams.sortDirection}
          onSortChange={handleSortFullName}
          field="firstName"
          activeField={tableParams.sortBy}
        >
          {label}
        </TableHeadCellSort>
      ),
      cellRenderer: (row, isLast) => {
        const name = formatFullName(
          row?.firstName,
          row?.lastName,
          DEFAULT_NAME
        );
        return hasPermission ? (
          <TableCellLink
            last={isLast}
            classes="w-[13%] wrap-anywhere min-w-[120px]"
          >
            <Link
              to={PATHS.ADMIN.PORTAL_USER_PAGE(row.id)}
              data-testid={`account-brands-cell-id-${row.id}`}
            >
              {name}
            </Link>
          </TableCellLink>
        ) : (
          <TableCellText last={isLast} classes="wrap-anywhere min-w-[150px]">
            {name}
          </TableCellText>
        );
      },
    },
    {
      key: "email",
      label: AccountUsersColumn.EMAIL,
      visible: flags[AccountUsersColumn.EMAIL],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast} classes="wrap-anywhere min-w-[150px]">
          {row?.email ?? "-"}
        </TableCellText>
      ),
    },
    {
      key: "groups",
      label: AccountUsersColumn.GROUPS,
      visible: flags[AccountUsersColumn.GROUPS],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) =>
        row?.groups?.length ? (
          <TableCellShowAll
            branches={row.groups}
            isLast={isLast}
            classes={cn(
              ENTITY_COLUMN_CONFIG.width,
              shouldAlignTopFor(row, "groups") &&
                ENTITY_COLUMN_CONFIG.alignClass
            )}
          />
        ) : (
          <TableCellText last={isLast} classes="w-[11%]">
            -
          </TableCellText>
        ),
    },
    {
      key: "brands",
      label: AccountUsersColumn.BRANDS,
      visible: flags[AccountUsersColumn.BRANDS],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) =>
        row.brands.length ? (
          <TableCellShowAll
            branches={row.brands}
            isLast={isLast}
            classes={cn(
              ENTITY_COLUMN_CONFIG.width,
              shouldAlignTopFor(row, "brands") &&
                ENTITY_COLUMN_CONFIG.alignClass
            )}
          />
        ) : (
          <TableCellText last={isLast} classes="w-[11%]">
            -
          </TableCellText>
        ),
    },
    {
      key: "branches",
      label: AccountUsersColumn.BRANCHES,
      visible: flags[AccountUsersColumn.BRANCHES],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) =>
        row.branches.length ? (
          <TableCellShowAll
            branches={row.branches}
            isLast={isLast}
            classes={cn(
              ENTITY_COLUMN_CONFIG.width,
              shouldAlignTopFor(row, "branches") &&
                ENTITY_COLUMN_CONFIG.alignClass
            )}
          />
        ) : (
          <TableCellText last={isLast} classes="w-[11%]">
            -
          </TableCellText>
        ),
    },
    {
      key: "createdAt",
      label: AccountUsersColumn.ACCOUNT_CREATED,
      visible: flags[AccountUsersColumn.ACCOUNT_CREATED],
      headerComponent: ({ label }) => (
        <TableHeadCellSort
          sortOrder={tableParams.sortDirection}
          onSortChange={handleSortCreatedAt}
          field="createdAt"
          activeField={tableParams.sortBy}
        >
          {label}
        </TableHeadCellSort>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {formatIsoDate(row?.createdAt) ?? "-"}
        </TableCellText>
      ),
    },
    {
      key: "lastActivityAt",
      label: AccountUsersColumn.LAST_ACTIVITY,
      visible: flags[AccountUsersColumn.LAST_ACTIVITY],
      headerComponent: ({ label }) => (
        <TableHeadCellSort
          sortOrder={tableParams.sortDirection}
          onSortChange={handleSortLastActivityAt}
          field="lastActivityAt"
          activeField={tableParams.sortBy}
        >
          {label}
        </TableHeadCellSort>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {formatIsoDate(row?.lastActivityAt) || "-"}
        </TableCellText>
      ),
    },
    {
      key: "status",
      label: AccountUsersColumn.USER_STATUS,
      visible: flags[AccountUsersColumn.USER_STATUS],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) =>
        row.status ? (
          <TableCellBadge
            last={isLast}
            stage={
              USERS_STATUS_VARIANT_MAP[formatEnumValue(row?.status)]?.badge
            }
          >
            {formatEnumValue(row?.status) ?? "-"}
          </TableCellBadge>
        ) : (
          <TableCellText last={isLast}>-</TableCellText>
        ),
    },
    {
      key: "assignments",
      label: AccountUsersColumn.ROLE,
      visible: flags[AccountUsersColumn.ROLE],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => {
        const roleNames = Array.from(
          (row?.assignments ?? []).reduce(
            (acc, a) => (
              a?.roleName && acc.add(formatEnumValue(a.roleName)),
              acc
            ),
            new Set<string>()
          )
        );

        return roleNames?.length ? (
          <TableCellShowAll branches={roleNames} isLast={isLast} />
        ) : (
          <TableCellText last={isLast}>-</TableCellText>
        );
      },
    },
  ];

  const { initialMap, initialObjects } = useMemo(() => {
    const roleIdFromUrl = urlParams?.roleIds;

    if (roleIdFromUrl && (usersRoleDatas ?? []).length > 0) {
      const foundRole = usersRoleDatas?.find(role => role.id === roleIdFromUrl);

      if (foundRole) {
        return {
          initialMap: {
            [AccountUsersColumn.ROLE]: [foundRole.name],
          },
          initialObjects: {
            [AccountUsersColumn.ROLE]: [foundRole],
          },
        };
      }
    }

    return {
      initialMap: {},
      initialObjects: {},
    };
  }, [urlParams?.roleIds, usersRoleDatas]);

  return (
    <>
      <FilterSearch
        menuList={MENU_LIST}
        subMenus={SUB_MENUS as Record<string, SubMenuItem[]>}
        subMenuRegistry={SUB_MENU_REGISTRY}
        statusVariant={USERS_STATUS_VARIANT_MAP}
        classes="mt-8"
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        handleSetFilter={handleSetFilter}
        initialSelectedMap={initialMap as Record<string, string[]>}
        initialSelectedObjectMap={
          initialObjects as Record<string, OptionItem[]>
        }
        resetObject={NO_ROLE}
      >
        <DropDownViewMenu
          viewList={TABLE_HEADERS}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
        />
      </FilterSearch>

      {isPending || isOptionPending ? (
        <Loader />
      ) : (
        <DynamicTable
          data={usersData?.items || []}
          columns={columns}
          searchValue={searchValue}
          next={handleNext}
          prev={handlePrev}
          tableParams={usersData}
          setTableParams={setTableParams}
        />
      )}
    </>
  );
};

export default AccountUsersTable;
